import React, { useContext } from 'react';
import { produce } from 'immer';
import {
  type AllConditions,
  type ConditionProperties,
} from 'json-rules-engine';
import { get, set, unset } from 'lodash';
import { SButton, SIcon } from '@chargebee/sting-react';
import { AndOrPillButton } from '@bbkAdminComponents/experiences/audiences/edit-rule/AndOrPillButton';
import { ConditionItem } from '@bbkAdminComponents/experiences/audiences/edit-rule/condition-item';
import { EditRuleContext } from '@bbkAdminComponents/experiences/audiences/edit-rule/edit-rule-context';
import {
  getGroupConditions,
  getGroupOperator,
  getGroupType,
  GroupOperator,
  GroupType,
  isGroup,
} from '@bbkAdminComponents/experiences/audiences/edit-rule/rules-utils';
import { cs } from '@bbkAdminUtils/css-utils';
import { type PatchPath } from '@bbkAdminUtils/utility-types';

type ConditionGroupProps = {
  renderBorder?: boolean;
  // Maximum remaining nesting levels allowed for sub-groups. Decremented when recursing.
  // When 1, adding new groups is disabled.
  maxLevels?: number;
  // Current nesting level (root = 0). Used for display/indentation and recursion guard.
  level?: number;
  path: PatchPath;
  // If true, disallows changing the grouping operator (AND/OR)
  disallowOperatorChange?: boolean;
};

// ATTN: Keep this as function, recursion doesn't work with const variables
export function ConditionsGroup(props: ConditionGroupProps) {
  const {
    path,
    renderBorder,
    maxLevels = 1,
    level = 0,
    disallowOperatorChange = false,
  } = props;

  const { rule, setRule } = useContext(EditRuleContext);

  const group = get(rule, path);
  const groupOperator = getGroupOperator(group);
  const groupConditions = getGroupConditions(group);
  const groupPath = [...path, groupOperator];
  const groupType = getGroupType(group);
  const shouldRenderColumnHeader = level === 0 && groupConditions.length > 0;

  const updateGroupingOperator = (checked: boolean) => {
    const newGroupingOperator: GroupOperator = checked
      ? GroupOperator.all
      : GroupOperator.any;

    const updatedRule = produce(rule, (draft) => {
      // swapping operators all|any
      set(draft, [...path, newGroupingOperator], get(draft, groupPath));

      unset(draft, groupPath);
    });

    setRule(updatedRule);
  };

  const addGroup = () => {
    const updatedRule = produce(rule, (draft) => {
      const curGroup = get(draft, groupPath);

      const newGroup: AllConditions = {
        all: [
          {
            fact: '',
            operator: '',
            value: '',
          },
        ],
      };
      curGroup.push(newGroup);
    });

    setRule(updatedRule);
  };

  const addCondition = () => {
    const updatedRule = produce(rule, (draft) => {
      const curGroup = get(draft, groupPath);

      const newCondition: ConditionProperties = {
        fact: '',
        operator: '',
        value: '',
      };
      curGroup.push(newCondition);
    });

    setRule(updatedRule);
  };

  return (
    <div
      className={cs(
        'tw-w-full tw-rounded-md tw-border tw-border-neutral-100 tw-bg-neutral-25 tw-p-xs',
        {
          'tw-bg-white': renderBorder,
        }
      )}
    >
      {groupConditions.length === 0 && (
        <div className="tw-pb-4 tw-text-neutral-500">No rules defined!</div>
      )}
      {shouldRenderColumnHeader && (
        <div className="tw-grid tw-grid-cols-[3.5rem_minmax(0,1fr)] tw-gap-sm tw-pb-ti">
          <div />
          <div className="tw-flex tw-gap-sm tw-text-xs tw-font-semibold tw-text-neutral-700">
            <div className="tw-min-w-0 tw-basis-[26%]">Property</div>
            <div className="tw-min-w-0 tw-basis-[26%]">Operator</div>
            <div className="tw-min-w-0 tw-flex-1">Value</div>
            <div className="tw-w-16" />
          </div>
        </div>
      )}
      <div className="tw-flex tw-flex-col">
        {groupConditions.map((condition, idx) => {
          const conditionPath = [...path, groupOperator, idx];
          const connectorLabel = level === 0 && idx === 0 ? 'WHEN' : groupType;

          return (
            <div
              key={conditionPath.join('.')}
              className={cs(
                'tw-grid tw-grid-cols-[3.5rem_minmax(0,1fr)] tw-items-start tw-gap-sm',
                'tw-border-b tw-border-neutral-100 tw-py-xs first:tw-pt-0 last:tw-border-b-0 last:tw-pb-0'
              )}
            >
              <div className="tw-pt-2 tw-text-xs tw-font-medium tw-uppercase tw-text-neutral-600">
                {connectorLabel}
              </div>
              <div className="tw-min-w-0">
                {isGroup(condition) ? (
                  <ConditionsGroup
                    path={conditionPath}
                    renderBorder
                    maxLevels={maxLevels - 1}
                    level={level + 1}
                    disallowOperatorChange={disallowOperatorChange}
                  />
                ) : (
                  <ConditionItem
                    condition={condition}
                    path={conditionPath}
                    showLabels={false}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div
        className={cs('tw-flex tw-flex-col tw-gap-4', {
          'tw-pt-4': groupConditions.length > 0,
        })}
      >
        {!disallowOperatorChange && groupConditions.length > 1 && (
          <AndOrPillButton
            value={getGroupType(group)}
            onChange={(v) => updateGroupingOperator(v === GroupType.AND)}
            disabled={disallowOperatorChange}
          />
        )}
        <div>
          <SButton
            variant="primary-ghost"
            icon={<SIcon name="plus" />}
            onClick={addCondition}
          >
            Add another condition
          </SButton>
          {maxLevels > 1 && (
            <SButton
              variant="primary-ghost"
              icon={<SIcon name="plus" />}
              onClick={addGroup}
            >
              Group of rules
            </SButton>
          )}
        </div>
      </div>
    </div>
  );
}
